import { useState, useEffect, CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../../utils/api';

// NOTE: this panel queries /api/db/tables globally, not scoped to the
// current bot — a pre-existing quirk from before the dashboard page split,
// intentionally not fixed here (out of scope for the routing refactor).
export default function OrdersRoute() {
  const [tables, setTables] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [activeTable, setActiveTable] = useState<any>(null);
  const [loadingTables, setLoadingTables] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);

  useEffect(() => {
    apiFetch('/api/db/tables').then(r => r.json()).then(d => {
      if (d.success) setTables(d.tables || []);
      setLoadingTables(false);
    }).catch(() => setLoadingTables(false));
  }, []);

  const openTable = async (t: any) => {
    setActiveTable(t); setLoadingRows(true); setRows([]);
    const r = await apiFetch(`/api/db/tables/${t.slug}/rows`);
    const d = await r.json();
    if (d.success) setRows(d.rows || []);
    setLoadingRows(false);
  };

  const card = { background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '12px', padding: '16px', marginBottom: '12px' } as CSSProperties;
  const pill = { display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' as const, background: 'var(--bg-element)', color: 'var(--text-muted)' } as CSSProperties;

  if (loadingTables) return <div style={{ padding: '40px', color: 'var(--text-faint)', fontSize: '13px' }}>Loading…</div>;

  if (tables.length === 0) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '36px', marginBottom: '12px' }}>🗄️</div>
      <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>No database tables yet</div>
      <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>Create an orders or leads table in the Database section, then use the API endpoint in your bot's flow to capture data here automatically.</div>
      <Link to="/database" style={{ background: '#5B4FE8', color: 'white', padding: '8px 20px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>Go to Database →</Link>
    </div>
  );

  return (
    <div>
      {/* Table selector */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' as const }}>
        {tables.map(t => (
          <button key={t.slug} onClick={() => openTable(t)} style={{
            padding: '7px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
            background: activeTable?.slug === t.slug ? '#5B4FE8' : 'var(--bg-element)',
            color: activeTable?.slug === t.slug ? 'white' : 'var(--text-muted)',
          }}>
            {t.name} <span style={{ opacity: 0.7, fontWeight: '400' }}>{t.rowCount}</span>
          </button>
        ))}
        <Link to="/database" style={{ padding: '7px 14px', borderRadius: '8px', background: 'var(--bg-element)', color: 'var(--text-muted)', textDecoration: 'none', fontSize: '12px', border: '1px dashed var(--card-border)' }}>+ Manage tables</Link>
      </div>

      {!activeTable && (
        <div style={{ ...card, textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Select a table above to view its rows</div>
        </div>
      )}

      {activeTable && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>{activeTable.name} <span style={{ ...pill, marginLeft: '6px' }}>{rows.length} rows</span></div>
          </div>
          {loadingRows && <div style={{ color: 'var(--text-faint)', fontSize: '13px' }}>Loading…</div>}
          {!loadingRows && rows.length === 0 && (
            <div style={{ ...card, textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No rows yet — they'll appear here when your flow writes data to this table.</div>
            </div>
          )}
          {!loadingRows && rows.length > 0 && (() => {
            const fields = activeTable.fields || [];
            return (
              <div style={{ overflowX: 'auto' as const }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                      {fields.map((f: any) => (
                        <th key={f.key} style={{ padding: '8px 12px', textAlign: 'left' as const, color: 'var(--text-muted)', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const }}>{f.name}</th>
                      ))}
                      <th style={{ padding: '8px 12px', textAlign: 'left' as const, color: 'var(--text-muted)', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' as const }}>Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...rows].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).map(row => (
                      <tr key={row.id} style={{ borderBottom: '1px solid var(--bg-element)' }}>
                        {fields.map((f: any) => (
                          <td key={f.key} style={{ padding: '10px 12px', color: 'var(--text-primary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{String(row[f.key] ?? '—')}</td>
                        ))}
                        <td style={{ padding: '10px 12px', color: 'var(--text-faint)', whiteSpace: 'nowrap' as const }}>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
